import { FC } from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Button,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Description,
  Person,
  CalendarToday,
  Business,
  Assignment,
  AttachFile,
  Timeline,
} from '@mui/icons-material';
import { useComplianceManagementState } from '../../contexts/complianceManagement.context';
import {
  ComplianceStatus,
  RiskLevel,
  Priority,
} from '../../../../../apis/settings/settings.Interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface ComplianceManagementDetailsProps {
  onBack: () => void;
}

const ComplianceManagementDetails: FC<ComplianceManagementDetailsProps> = ({
  onBack,
}) => {
  const { selectedCompliance, isLoadingDetails } =
    useComplianceManagementState();
  const { translateText } = useTranslation();

  if (isLoadingDetails) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>
          {translateText('settings.complianceManagement.loadingDetails')}
        </Typography>
      </Box>
    );
  }

  if (!selectedCompliance) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">
          {translateText('settings.complianceManagement.noComplianceSelected')}
        </Typography>
        <Button onClick={onBack} sx={{ mt: 2 }} startIcon={<ArrowBack />}>
          {translateText('settings.complianceManagement.backToList')}
        </Button>
      </Box>
    );
  }

  const getStatusColor = (status: ComplianceStatus) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'OVERDUE':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'WAIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRiskLevelColor = (riskLevel?: RiskLevel) => {
    switch (riskLevel) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority?: Priority) => {
    switch (priority) {
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      data-testid="compliance-management-details"
      sx={{ pb: { xs: 1, sm: 2 }, height: '100%', overflow: 'auto' }}
    >
      {/* Basic Information Card */}
      <Grid
        item
        xs={12}
        md={6}
        data-testid="compliance-management-details-basic-information"
      >
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: { xs: 1, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              <Description color="primary" />
              {translateText('settings.complianceManagement.basicInformation')}
            </Typography>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'settings.complianceManagement.fields.complianceId'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedCompliance.id}
                </Typography>
              </Grid>
              {selectedCompliance.code && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText('settings.complianceManagement.fields.code')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedCompliance.code}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('settings.complianceManagement.fields.title')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedCompliance.title}
                </Typography>
              </Grid>
              {selectedCompliance.description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.description'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.description}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">
                  {translateText('settings.complianceManagement.fields.status')}
                </Typography>
                <Chip
                  label={selectedCompliance.status}
                  size="small"
                  color={getStatusColor(selectedCompliance.status) as any}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              {selectedCompliance.riskLevel && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.riskLevel'
                    )}
                  </Typography>
                  <Chip
                    label={selectedCompliance.riskLevel}
                    size="small"
                    color={
                      getRiskLevelColor(selectedCompliance.riskLevel) as any
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              )}
              {selectedCompliance.priority && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.priority'
                    )}
                  </Typography>
                  <Chip
                    label={selectedCompliance.priority}
                    size="small"
                    color={getPriorityColor(selectedCompliance.priority) as any}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
              )}
              {selectedCompliance.category && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.category'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.category}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.subCategory && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.subCategory'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.subCategory}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.type && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText('settings.complianceManagement.fields.type')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.type}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Regulatory Information Card */}
      <Grid
        item
        xs={12}
        md={6}
        data-testid="compliance-management-details-regulatory-information"
      >
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              <Business color="primary" />
              {translateText(
                'settings.complianceManagement.regulatoryInformation'
              )}
            </Typography>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {selectedCompliance.jurisdiction && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.jurisdiction'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.jurisdiction}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.authority && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.authority'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.authority}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.sourceRefs &&
                selectedCompliance.sourceRefs.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'settings.complianceManagement.fields.sourceReferences'
                      )}
                    </Typography>
                    {selectedCompliance.sourceRefs.map((ref, index) => (
                      <Box key={index} sx={{ mt: 0.5 }}>
                        <Typography variant="body2">{ref.label}</Typography>
                        {ref.url && (
                          <Button
                            size="small"
                            href={ref.url}
                            target="_blank"
                            sx={{ mt: 0.5 }}
                          >
                            {translateText(
                              'settings.complianceManagement.fields.viewReference'
                            )}
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Grid>
                )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Dates Card */}
      <Grid
        item
        xs={12}
        md={6}
        data-testid="compliance-management-details-important-dates"
      >
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
              <CalendarToday color="primary" />
              {translateText('settings.complianceManagement.importantDates')}
            </Typography>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              {selectedCompliance.effectiveDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.effectiveDate'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      selectedCompliance.effectiveDate
                    ).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.dueDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.dueDate'
                    )}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(selectedCompliance.dueDate).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.nextDueDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.nextDueDate'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      selectedCompliance.nextDueDate
                    ).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.lastCompletedAt && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.lastCompletedAt'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      selectedCompliance.lastCompletedAt
                    ).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {selectedCompliance.recurrence && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'settings.complianceManagement.fields.recurrence'
                    )}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.recurrence.frequency}
                    {selectedCompliance.recurrence.interval &&
                      ` (Every ${selectedCompliance.recurrence.interval})`}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Owners Card */}
      {selectedCompliance.owners && selectedCompliance.owners.length > 0 && (
        <Grid
          item
          xs={12}
          md={6}
          data-testid="compliance-management-details-owners"
        >
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
              >
                <Person color="primary" />
                {translateText('settings.complianceManagement.owners')} (
                {selectedCompliance.owners.length})
              </Typography>
              <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {selectedCompliance.owners.map((owner, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          {owner.name || 'N/A'}
                        </Typography>
                        {owner.role && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            {translateText(
                              'settings.complianceManagement.fields.role'
                            )}
                            : {owner.role}
                          </Typography>
                        )}
                        {owner.type && (
                          <Chip
                            label={owner.type}
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Related Entity Card */}
      {selectedCompliance.relatedEntity && (
        <Grid
          item
          xs={12}
          md={6}
          data-testid="compliance-management-details-related-entity"
        >
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
              >
                <Business color="primary" />
                {translateText('settings.complianceManagement.relatedEntity')}
              </Typography>
              <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText('settings.complianceManagement.fields.type')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.relatedEntity.type}
                  </Typography>
                </Grid>
                {selectedCompliance.relatedEntity.name && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      {translateText(
                        'settings.complianceManagement.fields.name'
                      )}
                    </Typography>
                    <Typography variant="body1">
                      {selectedCompliance.relatedEntity.name}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {translateText('settings.complianceManagement.fields.id')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedCompliance.relatedEntity.id}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Checklist Card */}
      {selectedCompliance.checklist &&
        selectedCompliance.checklist.length > 0 && (
          <Grid
            item
            xs={12}
            data-testid="compliance-management-details-checklist"
          >
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
                  <Assignment color="primary" />
                  {translateText('settings.complianceManagement.checklist')} (
                  {selectedCompliance.checklist.length})
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {selectedCompliance.checklist.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            {item.label}
                          </Typography>
                          <Chip
                            label={item.status}
                            size="small"
                            color={
                              item.status === 'DONE'
                                ? 'success'
                                : item.status === 'PENDING'
                                  ? 'warning'
                                  : 'default'
                            }
                            sx={{ mt: 1 }}
                          />
                          {item.isMandatory && (
                            <Chip
                              label={translateText(
                                'settings.complianceManagement.fields.mandatory'
                              )}
                              size="small"
                              color="error"
                              sx={{ mt: 1, ml: 1 }}
                            />
                          )}
                          {item.dueDate && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              sx={{ mt: 1 }}
                            >
                              {translateText(
                                'settings.complianceManagement.fields.due'
                              )}
                              : {new Date(item.dueDate).toLocaleDateString()}
                            </Typography>
                          )}
                          {item.completedAt && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              sx={{ mt: 1 }}
                            >
                              {translateText(
                                'settings.complianceManagement.fields.completed'
                              )}
                              :{' '}
                              {new Date(item.completedAt).toLocaleDateString()}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

      {/* Documents Card */}
      {selectedCompliance.documents &&
        selectedCompliance.documents.length > 0 && (
          <Grid
            item
            xs={12}
            data-testid="compliance-management-details-documents"
          >
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
                  <AttachFile color="primary" />
                  {translateText('settings.complianceManagement.documents')} (
                  {selectedCompliance.documents.length})
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {selectedCompliance.documents.map((doc, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={doc.documentId || index}
                    >
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            gutterBottom
                          >
                            {doc.description || doc.documentName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                          >
                            {translateText(
                              'settings.complianceManagement.fields.documentType'
                            )}
                            : {doc.documentType}
                          </Typography>
                          {doc.fileUrl && (
                            <Button
                              size="small"
                              href={doc.fileUrl}
                              target="_blank"
                              sx={{ mt: 1 }}
                            >
                              {translateText(
                                'settings.complianceManagement.fields.viewDocument'
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

      {/* Tags Card */}
      {selectedCompliance.tags && selectedCompliance.tags.length > 0 && (
        <Grid item xs={12} data-testid="compliance-management-details-tags">
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                sx={{ 
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {translateText('settings.complianceManagement.tags')}
              </Typography>
              <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedCompliance.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Activity Log Card */}
      {selectedCompliance.activityLog &&
        selectedCompliance.activityLog.length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
                  <Timeline color="primary" />
                  {translateText('settings.complianceManagement.activityLog')} (
                  {selectedCompliance.activityLog.length})
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                  {selectedCompliance.activityLog.map((activity) => (
                    <Grid item xs={12} key={activity.id}>
                      <Box
                        sx={{
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                          pl: 2,
                        }}
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {activity.message}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(activity.timestamp).toLocaleString()}
                          {activity.actor?.name && ` • ${activity.actor.name}`}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

      {/* Metadata Card */}
      {selectedCompliance.metadata &&
        Object.keys(selectedCompliance.metadata).length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Typography 
                variant="h6" 
                fontWeight="bold" 
                sx={{ 
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                  {translateText('settings.complianceManagement.metadata')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                  {Object.entries(selectedCompliance.metadata).map(
                    ([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography variant="caption" color="textSecondary">
                          {key}
                        </Typography>
                        <Typography variant="body1">{String(value)}</Typography>
                      </Grid>
                    )
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
    </Grid>
  );
};

export default ComplianceManagementDetails;
